import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useUser } from '../context/UserContext';
import './profile.css';

const COUNTRY_OPTIONS = [
    { code: 'US', label: 'United States (+1)' },
    { code: 'CA', label: 'Canada (+1)' },
    { code: 'MX', label: 'Mexico (+52)' },
    { code: 'IN', label: 'India (+91)' }
];

const toLocalPhoneDigits = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
};

const toShortDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString();
};

const ProfilePage = () => {
    const navigate = useNavigate();
    const { user, setUser } = useUser();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [form, setForm] = useState({
        display: '',
        firstName: '',
        lastName: '',
        country: '',
        phone: '',
        email: '',
        role: '',
        createdAt: '',
        lastLogin: ''
    });

    const initials = useMemo(() => {
        const base = String(form.display || [form.firstName, form.lastName].filter(Boolean).join(' ')).trim();
        if (!base) return 'U';
        const words = base.split(/\s+/).filter(Boolean);
        if (words.length === 1) return words[0].slice(0, 1).toUpperCase();
        return `${words[0].slice(0, 1)}${words[1].slice(0, 1)}`.toUpperCase();
    }, [form.display, form.firstName, form.lastName]);

    const loadProfile = async () => {
        setLoading(true);
        try {
            const response = await api.get('/user/me');
            const payload = response?.data || {};
            setForm({
                display: String(payload.display || '').trim(),
                firstName: String(payload.firstName || '').trim(),
                lastName: String(payload.lastName || '').trim(),
                country: String(payload.country || '').trim(),
                phone: toLocalPhoneDigits(payload.phone),
                email: String(payload.email || '').trim(),
                role: String(payload.role || '').trim(),
                createdAt: payload.createdAt || '',
                lastLogin: payload.lastLogin || ''
            });
        } catch (e) {
            setMessage({ type: 'error', text: 'Unable to load your profile right now.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value
        }));
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        setMessage({ type: '', text: '' });

        const payload = {
            display: String(form.display || '').trim(),
            firstName: String(form.firstName || '').trim(),
            lastName: String(form.lastName || '').trim(),
            country: String(form.country || '').trim(),
            phone: String(form.phone || '').trim()
        };

        if (!payload.display) {
            setMessage({ type: 'error', text: 'Display name is required.' });
            return;
        }

        if (payload.phone) {
            if (!payload.country) {
                setMessage({ type: 'error', text: 'Please select a country before saving a phone number.' });
                return;
            }

            if (!/^\d{10}$/.test(payload.phone)) {
                setMessage({ type: 'error', text: 'Phone number must be exactly 10 digits.' });
                return;
            }
        }

        setSaving(true);
        try {
            const response = await api.put('/user/me', payload);
            const updated = response?.data || {};

            setForm((prev) => ({
                ...prev,
                display: String(updated.display || '').trim(),
                firstName: String(updated.firstName || '').trim(),
                lastName: String(updated.lastName || '').trim(),
                country: String(updated.country || '').trim(),
                phone: toLocalPhoneDigits(updated.phone),
                email: String(updated.email || prev.email || '').trim(),
                role: String(updated.role || prev.role || '').trim(),
                createdAt: updated.createdAt || prev.createdAt,
                lastLogin: updated.lastLogin || prev.lastLogin
            }));

            const localUser = JSON.parse(localStorage.getItem('user') || '{}');
            const nextUser = {
                ...localUser,
                display: String(updated.display || localUser.display || '').trim()
            };
            localStorage.setItem('user', JSON.stringify(nextUser));
            setUser(nextUser);

            setMessage({ type: 'success', text: 'Profile saved.' });
        } catch (e) {
            const backendError = e?.response?.data?.error;
            setMessage({ type: 'error', text: backendError || 'Failed to save profile.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="profile-page">
            <div className="profile-cover">
                <button className="btn-secondary profile-back-btn" onClick={() => navigate(-1)}>
                    Back
                </button>
            </div>

            <div className="profile-header-card">
                <div className="profile-avatar">{initials}</div>
                <div className="profile-identity">
                    <h1>{form.display || 'My Profile'}</h1>
                    <p>{form.email || (user?.email || '')}</p>
                    <div className="profile-meta-chips">
                        <span>Role: {form.role || (user?.role || 'host')}</span>
                        <span>Created: {toShortDate(form.createdAt)}</span>
                        <span>Last Login: {toShortDate(form.lastLogin)}</span>
                    </div>
                </div>
            </div>

            <section className="profile-form-card">
                <h2>Account Details</h2>
                {message.text && (
                    <p className={message.type === 'success' ? 'profile-message profile-message-success' : 'profile-message profile-message-error'}>
                        {message.text}
                    </p>
                )}

                {loading ? (
                    <p>Loading profile...</p>
                ) : (
                    <form className="profile-form" onSubmit={onSubmit}>
                        <label>
                            Display Name
                            <input name="display" value={form.display} onChange={onChange} required />
                        </label>
                        <label>
                            First Name
                            <input name="firstName" value={form.firstName} onChange={onChange} />
                        </label>
                        <label>
                            Last Name
                            <input name="lastName" value={form.lastName} onChange={onChange} />
                        </label>
                        <label>
                            Country
                            <select name="country" value={form.country} onChange={onChange}>
                                <option value="">Select country</option>
                                {COUNTRY_OPTIONS.map((option) => (
                                    <option key={option.code} value={option.code}>{option.label}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Phone Number
                            <input name="phone" value={form.phone} onChange={onChange} inputMode="numeric" placeholder="10 digits" maxLength={10} />
                        </label>
                        <label>
                            Email
                            <input name="email" value={form.email} disabled />
                        </label>
                        <div className="profile-form-actions">
                            <button className="btn-primary" type="submit" disabled={saving}>
                                {saving ? 'Saving...' : 'Save Profile'}
                            </button>
                        </div>
                    </form>
                )}
            </section>
        </div>
    );
};

export default ProfilePage;
